// /src/hooks/useGroupClosing.js
// Whether a group is closing, and the owner's way back (Roadmap #103; docs/briefs/
// 2026-10-16-group-being-closed.md with addenda 1–2).
//
// One place computes canUndo, so every surface that shows the closing notice (group page, admin
// page, edit form, owner card) agrees on who may undo — and a future rule that widens undo beyond
// the owner (#79, members taking charge) changes it here, not at each mount. The server still
// decides: undo_soft_delete_group checks the caller itself.
//
// The banner that renders this state is presentational (GroupClosingBanner, via NoticeStrip); the
// page that mounts it calls this hook and passes the state down.

import { useCallback, useEffect, useState } from 'react';
import { groupService } from '../data/dataService';

/**
 * @param {string|null} groupId
 * @param {{id?: string}|null} currentUser
 * @param {{closing?: boolean, closesOn?: string|null, ownerId?: string|null}|null} [known] -
 *   closing state the caller already holds (e.g. a group card row), so no read is needed
 * @returns {{ closing: boolean, closesOn: string|null, canUndo: boolean, undoing: boolean,
 *   undoFailed: boolean, undo: Function, refresh: Function }}
 */
export function useGroupClosing(groupId, currentUser, known = null) {
  const [state, setState] = useState(known);
  const [undoing, setUndoing] = useState(false);
  const [undoFailed, setUndoFailed] = useState(false);
  const knownClosing = known?.closing;
  const knownClosesOn = known?.closesOn;
  const knownOwnerId = known?.ownerId;

  const refresh = useCallback(async () => {
    if (!groupId) { setState(null); return; }
    const next = await groupService.getGroupClosingState(groupId);
    setState(next);
  }, [groupId]);

  useEffect(() => {
    setUndoFailed(false);
    if (knownClosing !== undefined) {
      setState({ closing: !!knownClosing, closesOn: knownClosesOn || null, ownerId: knownOwnerId || null });
      return;
    }
    refresh();
  }, [groupId, knownClosing, knownClosesOn, knownOwnerId, refresh]);

  const closing = !!state?.closing;
  const canUndo = closing && !!currentUser?.id && state?.ownerId === currentUser.id;

  // Returns true when the group is open again. A refusal (success:false, its English error) is
  // never shown as text: the banner shows a translated failure instead (Advisor ADVISE 6).
  const undo = useCallback(async () => {
    if (!canUndo || undoing) return false;
    setUndoing(true);
    setUndoFailed(false);
    const result = await groupService.undoSoftDelete(groupId, currentUser.id);
    setUndoing(false);
    if (result?.success) {
      setState(prev => ({ ...(prev || {}), closing: false, closesOn: null }));
      return true;
    }
    setUndoFailed(true);
    return false;
  }, [canUndo, undoing, groupId, currentUser?.id]);

  return { closing, closesOn: state?.closesOn || null, canUndo, undoing, undoFailed, undo, refresh };
}
